import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { isMainSearchTypeKeywords } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Heading, Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import Footer from '../../components/Footer/Footer';

import SearchForm from './SearchForm/SearchForm';

import css from './NotFoundPage.module.css';

/**
 * @param {Object} props
 * @param {import('react-router-dom').History} props.history
 * @param {Array} props.routeConfiguration
 * @param {string} props.marketplaceName
 * @param {boolean} props.isKeywordSearch
 * @param {import('react-intl').IntlShape} props.intl
 * @param {boolean} props.scrollingDisabled
 * @param {Object} [props.staticContext] - StaticRouter context (SSR): sets HTTP 404 when present
 */
export const NotFoundPageComponent = props => {
  const {
    history,
    routeConfiguration,
    marketplaceName,
    isKeywordSearch,
    intl,
    scrollingDisabled,
    staticContext,
  } = props;

  if (staticContext) {
    staticContext.notfound = true;
  }

  const title = intl.formatMessage({
    id: 'NotFoundPage.title',
  });

  const handleSearchSubmit = values => {
    const { keywords, location } = values;
    const { search, selectedPlace } = location || {};
    const { origin, bounds } = selectedPlace || {};
    const searchParams = keywords ? { keywords } : { address: search, origin, bounds };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<Footer />}>
        <div className={css.root}>
          <section className={css.hero}>
            <div className={css.heroBg} aria-hidden />
            <div className={css.heroOrb} aria-hidden />
            <div className={css.heroInner}>
              <span className={css.eyebrow}>
                <FormattedMessage id="NotFoundPage.eyebrow" />
              </span>
              <Heading as="h1" rootClassName={css.heroHeading}>
                <FormattedMessage
                  id="NotFoundPage.heading"
                  values={{
                    em: chunks => <em>{chunks}</em>,
                  }}
                />
              </Heading>
              <p className={css.heroDescription}>
                <FormattedMessage id="NotFoundPage.description" values={{ marketplaceName }} />
              </p>
              <div className={css.heroActions}>
                <NamedLink name="LandingPage" className={css.heroCta}>
                  <FormattedMessage id="NotFoundPage.ctaHome" />
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </NamedLink>
                <NamedLink name="SearchPage" className={css.heroSecondary}>
                  <FormattedMessage id="NotFoundPage.ctaBrowse" />
                </NamedLink>
              </div>
            </div>
          </section>

          <section className={css.searchSection}>
            <SearchForm
              rootClassName={css.searchFormRoot}
              isKeywordSearch={isKeywordSearch}
              onSubmit={handleSearchSubmit}
            />
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The not-found page.
 *
 * @param {Object} props
 * @param {Object} props.staticContext - StaticRouter context; pass through when this page is nested.
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled for the layout chrome.
 * @returns {JSX.Element}
 */
const EnhancedNotFoundPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();

  return (
    <NotFoundPageComponent
      routeConfiguration={routeConfiguration}
      marketplaceName={config.marketplaceName}
      isKeywordSearch={isMainSearchTypeKeywords(config)}
      history={history}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const NotFoundPage = compose(connect(mapStateToProps))(EnhancedNotFoundPage);

export default NotFoundPage;
